import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { inicializarNotificacoes } from "../services/notificationService";
import { getLegalReadiness, recordLegalAcceptance, updateMarketingPreference } from "../services/legalService";
import logoZuno from "../assets/zuno-logo.png";
import { canContinueLegal, nextExperiencePhase, REQUIRED_PASSENGER_DOCUMENTS } from "./experiencePolicy";
import zunoSymbol from "../assets/zuno-splash.png";
import { isStoreScreenshotCapture } from "../screenshotSession";
import { getCurrentLocation, persistLocationState } from "../services/locationService";
import { auth } from "../firebase";
import { PASSENGER_TERMS_VERSION } from "../legal/Terms";
import { PRIVACY_VERSION } from "../legal/Privacy";
import { markPerformance } from "../services/performanceTrace";

const requiredTypes = REQUIRED_PASSENGER_DOCUMENTS;
const LEGAL_WRITE_TIMEOUT_MS = 12000;
const LEGAL_CACHE_VERSION = `${PASSENGER_TERMS_VERSION}|${PRIVACY_VERSION}`;
const legalCacheKey = () => auth.currentUser?.uid ? `zunoLegalReady:${auth.currentUser.uid}` : null;
const hasCurrentLegalCache = () => {
  const key = legalCacheKey();
  return Boolean(key && localStorage.getItem(key) === LEGAL_CACHE_VERSION);
};
const phaseAfterLegal = () => nextExperiencePhase({
  legalReady: true,
  locationPrimer: localStorage.getItem("zunoLocationPrimer"),
  notificationPrimer: localStorage.getItem("zunoNotificationPrimer"),
});

const withLegalWriteTimeout = (promise) => new Promise((resolve, reject) => {
  const timeoutId = window.setTimeout(() => {
    const cause = new Error("O registro do aceite demorou demais. Tente novamente.");
    cause.code = "legal/write-timeout";
    reject(cause);
  }, LEGAL_WRITE_TIMEOUT_MS);
  promise.then(
    (value) => { window.clearTimeout(timeoutId); resolve(value); },
    (cause) => { window.clearTimeout(timeoutId); reject(cause); },
  );
});

export default function PassengerExperienceGate({ children }) {
  const [phase, setPhase] = useState(() => (
    isStoreScreenshotCapture ? "done" : hasCurrentLegalCache() ? phaseAfterLegal() : "loading"
  ));
  const [accepted, setAccepted] = useState({ TERMS_PASSENGER: false, PRIVACY_NOTICE: false });
  const [marketing, setMarketing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isStoreScreenshotCapture) return undefined;
    const hadCurrentCache = hasCurrentLegalCache();
    markPerformance("LEGAL_STATUS_START", { cached: hadCurrentCache });
    let active = true;
    let timeoutId;
    const readinessTimeout = new Promise((_, reject) => {
      timeoutId = window.setTimeout(() => {
        const timeoutError = new Error("Legal readiness timed out.");
        timeoutError.code = "legal/timeout";
        reject(timeoutError);
      }, 12000);
    });
    Promise.race([getLegalReadiness("passenger"), readinessTimeout]).then(async (state) => {
      if (!active) return;
      window.clearTimeout(timeoutId);
      markPerformance("LEGAL_STATUS_DONE", { ready: state.ready });
      const key = legalCacheKey();
      if (key && state.ready) localStorage.setItem(key, LEGAL_CACHE_VERSION);
      if (key && !state.ready) localStorage.removeItem(key);
      setPhase(nextExperiencePhase({ legalReady: state.ready, locationPrimer: localStorage.getItem("zunoLocationPrimer"), notificationPrimer: localStorage.getItem("zunoNotificationPrimer") }));
      setMarketing(state.marketing === true);
    }).catch((cause) => {
      window.clearTimeout(timeoutId);
      markPerformance("LEGAL_STATUS_DONE", { status: "error", code: cause?.code || "unknown" });
      if (active && !hadCurrentCache) setPhase("legal");
    });
    return () => { active = false; window.clearTimeout(timeoutId); };
  }, []);

  const finishLegal = async () => {
    if (!canContinueLegal(accepted)) { setError("Leia e aceite os documentos obrigatórios para continuar."); return; }
    setSaving(true); setError("");
    const startedAt = performance.now();
    markPerformance("LEGAL_ACCEPT_CLICK");
    try {
      markPerformance("LEGAL_WRITE_START");
      const result = await withLegalWriteTimeout(recordLegalAcceptance(requiredTypes));
      markPerformance("LEGAL_WRITE_SUCCESS", { elapsedMs: Math.round(performance.now() - startedAt) });
      const key = legalCacheKey();
      if (key) localStorage.setItem(key, LEGAL_CACHE_VERSION);
      markPerformance("LEGAL_LOCAL_STATE_UPDATED", { versions: result?.versions?.length || 0 });
      updateMarketingPreference(marketing, "passenger_onboarding").catch((cause) => {
        console.warn("[ZunoExperience]", { stage: "MARKETING_WRITE_ERROR", code: cause?.code || "unknown", message: cause?.message || "unknown" });
      });
      markPerformance("LEGAL_NAVIGATION");
      setPhase(localStorage.getItem("zunoLocationPrimer") ? "notifications" : "location");
    } catch (e) {
      console.error("[ZunoExperience]", { stage: "TERMS_WRITE_ERROR", code: e?.code || "unknown", message: e?.message || "unknown", elapsedMs: Math.round(performance.now() - startedAt) });
      setError(`${e?.message || "Não foi possível registrar seu aceite."}${e?.code ? ` (${e.code})` : ""}`);
    }
    finally { setSaving(false); }
  };

  const locationChoice = async (request) => {
    localStorage.setItem("zunoLocationPrimer", request ? "accepted" : "skipped");
    if (!request) { persistLocationState("denied"); setPhase("notifications"); return; }
    try {
      const position = await getCurrentLocation({ requestPermission: true });
      persistLocationState("granted", position);
    } catch (cause) {
      persistLocationState(cause?.reason || "denied");
      console.warn("[ZunoLocation]", { stage: "PRIMER_ERROR", code: cause?.code || "unknown", message: cause?.message || "unknown" });
    }
    setPhase("notifications");
  };

  const notificationChoice = async (request) => {
    localStorage.setItem("zunoNotificationPrimer", request ? "accepted" : "skipped");
    setPhase("done");
    if (request) inicializarNotificacoes("passenger").catch(() => null);
  };

  if (phase === "done") return children;
  if (phase === "loading") return (
    <main className="zuno-experience-loading" aria-live="polite" aria-busy="true">
      <div className="zuno-experience-loading-aura" aria-hidden="true" />
      <img className="zuno-experience-loading-symbol" src={zunoSymbol} alt="" />
      <span className="zuno-visually-hidden">Preparando sua experiência Zuno.</span>
    </main>
  );
  return <main className="zuno-app-shell" style={styles.shell}>
    <section style={styles.card} aria-live="polite">
      <img src={logoZuno} alt="Zuno" style={{ width: 112, marginBottom: 22 }} />
      {phase === "legal" && <>
        <small style={styles.eyebrow}>ANTES DE CONTINUAR</small>
        <h1 style={styles.title}>Clareza desde o começo</h1>
        <p style={styles.text}>Os Termos regem o uso da plataforma. O Aviso de Privacidade explica os tratamentos necessários à conta, corridas, segurança, pagamentos e suporte.</p>
        <label style={styles.check}><input type="checkbox" checked={accepted.TERMS_PASSENGER} onChange={(e) => setAccepted((v) => ({ ...v, TERMS_PASSENGER: e.target.checked }))} /> <span>Li e aceito os <Link to="/termos">Termos de Uso da Zuno</Link>.</span></label>
        <label style={styles.check}><input type="checkbox" checked={accepted.PRIVACY_NOTICE} onChange={(e) => setAccepted((v) => ({ ...v, PRIVACY_NOTICE: e.target.checked }))} /> <span>Li o <Link to="/privacidade">Aviso de Privacidade</Link> apresentado nesta etapa.</span></label>
        <label style={{ ...styles.check, opacity: .92 }}><input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} /> <span>Quero receber novidades, promoções e ofertas. <b>Opcional.</b></span></label>
        {error && <p role="alert" style={{ color: "#ff8585" }}>{error}</p>}
        <button style={styles.primary} disabled={saving} onClick={finishLegal}>{saving ? "Registrando…" : "Continuar"}</button>
      </>}
      {phase === "location" && <>
        <small style={styles.eyebrow}>LOCALIZAÇÃO</small><h1 style={styles.title}>Encontre seu ponto de partida</h1>
        <p style={styles.text}>A Zuno usa sua localização quando necessário para definir a origem, calcular rotas e conectar você a motoristas próximos. Você também pode digitar o endereço manualmente.</p>
        <button style={styles.primary} onClick={() => locationChoice(true)}>Continuar</button>
        <button style={styles.secondary} onClick={() => locationChoice(false)}>Agora não</button>
      </>}
      {phase === "notifications" && <>
        <small style={styles.eyebrow}>NOTIFICAÇÕES</small><h1 style={styles.title}>Receba atualizações da corrida</h1>
        <p style={styles.text}>Avisaremos quando um motorista aceitar, chegar ao embarque ou quando houver uma alteração importante. Isso é opcional.</p>
        <button style={styles.primary} onClick={() => notificationChoice(true)}>Ativar notificações</button>
        <button style={styles.secondary} onClick={() => notificationChoice(false)}>Agora não</button>
      </>}
    </section>
  </main>;
}

const styles = {
  shell: { minHeight: "100dvh", display: "grid", placeItems: "center", padding: "max(20px, env(safe-area-inset-top)) 20px max(20px, env(safe-area-inset-bottom))", background: "linear-gradient(155deg,#07152b,#15103b)" },
  card: { width: "min(100%,460px)", padding: "32px 26px", borderRadius: 26, background: "rgba(10,20,43,.94)", border: "1px solid rgba(90,212,255,.2)", color: "#f7fbff", boxShadow: "0 25px 80px rgba(0,0,0,.35)" },
  eyebrow: { color: "#26e0c2", fontWeight: 800, letterSpacing: ".12em" }, title: { fontSize: 28, margin: "10px 0" },
  text: { color: "#b9c9df", lineHeight: 1.6 }, check: { display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 0", lineHeight: 1.45 },
  primary: { width: "100%", minHeight: 50, border: 0, borderRadius: 16, marginTop: 12, background: "linear-gradient(135deg,#15d7bd,#397dff)", color: "#031526", fontWeight: 850, fontSize: 16, cursor: "pointer" },
  secondary: { width: "100%", minHeight: 48, border: "1px solid rgba(255,255,255,.2)", borderRadius: 16, marginTop: 10, background: "transparent", color: "#eaf4ff", fontWeight: 700, cursor: "pointer" },
};
